import { forwardRef, type FocusEvent, type SelectHTMLAttributes } from 'react';

import { Field, type FieldWidth } from '../Field/Field';
import styles from './Select.module.css';

/**
 * Select — a short, fixed list of choices.
 *
 * Handoff, Forms 5: "Any select over ~8 options is a combobox with type-ahead.
 * Dispensary lists are long." That is a rule about which component to reach
 * for, so the component enforces it: a Select handed more than eight options
 * says so in development. The threshold is the handoff's, approximate sign and
 * all — nine is the first count that is unambiguously "over ~8".
 *
 * Native `<select>` on purpose. A hand-rolled listbox would have to
 * reimplement type-ahead, touch pickers, `optgroup`, and the platform's own
 * keyboard contract, and would lose the OS picker on mobile. The one thing a
 * native select cannot do is filter, which is exactly the line the handoff
 * draws at ~8 options.
 */

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'required' | 'className' | 'id'> {
  label: string;
  width: FieldWidth;
  options: readonly SelectOption[];
  /**
   * Rendered as a disabled, non-selectable first entry. Omit it for a field
   * that always has a value — an empty option the agent can re-select is a way
   * to clear a required field without noticing.
   */
  placeholder?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  /** Called on blur with the current value. Never called on change. */
  onValidate?: (value: string) => void;
  id?: string;
  className?: string;
}

/**
 * Handoff, Forms 5: "any select over ~8 options is a combobox".
 *
 * Not exported: `react-refresh/only-export-components` (task 18 §6) requires a
 * module that exports a component to export nothing but components and types.
 */
const MAX_OPTIONS = 8;

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    width,
    options,
    placeholder,
    error,
    hint,
    required = false,
    onValidate,
    id,
    className,
    disabled = false,
    onBlur,
    ...rest
  },
  ref,
) {
  if (import.meta.env.DEV && options.length > MAX_OPTIONS) {
    console.error(
      `Select ("${label}") was given ${options.length} options. The handoff puts the ` +
        `line at ~${MAX_OPTIONS}: past it a native select is a scroll-and-hunt ` +
        'control. Use Combobox, which filters.',
    );
  }

  function handleBlur(event: FocusEvent<HTMLSelectElement>) {
    onValidate?.(event.target.value);
    onBlur?.(event);
  }

  return (
    <Field
      label={label}
      width={width}
      required={required}
      error={error}
      hint={hint}
      disabled={disabled}
      id={id}
      className={className}
    >
      {({ widthClassName, className: controlClassName, ...control }) => (
        <select
          {...rest}
          {...control}
          ref={ref}
          className={[controlClassName, widthClassName, styles['select']].filter(Boolean).join(' ')}
          onBlur={handleBlur}
        >
          {placeholder === undefined ? null : (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
});
